import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { renderMarkdown } from "../lib/markdown";
import { getPage, deletePage, PageRow, PageVersionRow } from "../lib/api";
import { useSpaceContext } from "./SpaceLayout";

type PageDetail = { published_version: PageVersionRow | null } & PageRow;

export default function PageViewPage({ pageSlug: pageSlugProp }: { pageSlug?: string }) {
  const { username, spaceSlug, pageSlug: pageSlugParam } = useParams<{
    username: string;
    spaceSlug: string;
    pageSlug: string;
  }>();
  const pageSlug = pageSlugProp ?? pageSlugParam ?? "";
  const navigate = useNavigate();

  const { refreshPages, isOwner } = useSpaceContext();

  const [page, setPage] = useState<PageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!username || !spaceSlug || !pageSlug) return;
    setLoading(true);
    setError(null);
    getPage(username, spaceSlug, pageSlug)
      .then(setPage)
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Failed to load page");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [username, spaceSlug, pageSlug]);

  const renderedHtml =
    page?.published_version?.content != null
      ? renderMarkdown(page.published_version.content)
      : null;

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-8 py-10">
            {loading && (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/5 mb-2" />
                <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded w-1/4 mb-8" />
                <div className="space-y-3">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-4 bg-gray-100 dark:bg-gray-800 rounded"
                      style={{ width: `${55 + (i * 7) % 40}%` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
                {error}
              </div>
            )}

            {!loading && !error && page && (
              <>
                <div className="mb-8 flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {page.title}
                    </h1>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      /{page.slug}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/${username}/${spaceSlug}/${pageSlug}/history`}
                      className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                    >
                      History
                    </Link>
                    {isOwner && (
                      <>
                        <button
                          onClick={() => navigate(`/${username}/${spaceSlug}/${pageSlug}/edit`)}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
                        >
                          Edit
                        </button>
                        {!deleteConfirm ? (
                          <button
                            onClick={() => setDeleteConfirm(true)}
                            className="px-3 py-2 text-xs text-red-500 dark:text-red-400 hover:text-red-700 border border-red-200 dark:border-red-800 rounded-lg transition-colors"
                          >
                            Delete
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-red-600 dark:text-red-400">Delete page?</span>
                            <button
                              onClick={async () => {
                                if (!username || !spaceSlug) return;
                                setDeleting(true);
                                try {
                                  await deletePage(username, spaceSlug, pageSlug);
                                  refreshPages();
                                  navigate(`/${username}/${spaceSlug}`);
                                } finally {
                                  setDeleting(false);
                                }
                              }}
                              disabled={deleting}
                              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                            >
                              {deleting ? "..." : "Yes, delete"}
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(false)}
                              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {isOwner && page.has_draft ? (
                  <Link
                    to={`/${username}/${spaceSlug}/${pageSlug}/edit`}
                    className="flex items-center gap-2 mb-6 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                  >
                    <span className="w-2 h-2 rounded-full bg-amber-400 dark:bg-amber-500 shrink-0" />
                    <span>Draft waiting for review — click to review</span>
                    <span className="ml-auto">→</span>
                  </Link>
                ) : null}

                {renderedHtml ? (
                  <div
                    className="prose dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: renderedHtml }}
                  />
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                    No published content yet.
                  </p>
                )}
              </>
            )}
      </div>
    </main>
  );
}
